import * as Sentry from "@sentry/react";

export enum CoreErrorCause {
  InvalidCredential = "InvalidCredential",
  EmailAlreadyInUse = "EmailAlreadyInUse",
  WeakPassword = "WeakPassword",
  RequireRecentAuthentication = "RequireRecentAuthentication",
  PartnershipNotFound = "PartnershipNotFound",
  InvalidInvitationCode = "InvalidInvitationCode",
  InvitationCodeAlreadyUsed = "InvitationCodeAlreadyUsed",
  PartnerNotFound = "PartnerNotFound",
  InvalidPartnershipStatus = "InvalidPartnershipStatus",
  DataNotFound = "DataNotFound",
  Forbidden = "Forbidden",
  NetworkConnection = "NetworkConnection",
  PermissionDenied = "PermissionDenied",
  NetworkError = "NetworkError",
  PushNotificationsRequestFailure = "PushNotificationsRequestFailure",
  ConstantsCouldNotBeParsed = "ConstantsCouldNotBeParsed",
  PurchaseLoginFailed = "PurchaseLoginFailed",
  FetchingPackagesFailed = "FetchingPackagesFailed",
  PurchaseOfferingsFetchFailed = "PurchaseOfferingsFetchFailed",
  PurchasedPackageCannotBeFound = "PurchasedPackageCannotBeFound",
  PurchaseCancelledByUser = "PurchaseCancelledByUser",
  PurchaseFailed = "PurchaseFailed",
  PurchaseRestoreFailed = "PurchaseRestoreFailed",
  NoActiveSubscriptionFound = "NoActiveSubscriptionFound",
  PlatformNotSupported = "PlatformNotSupported",
  PurchaseConfirmationFailed = "PurchaseConfirmationFailed",
  PurchaseGrantTimeout = "PurchaseGrantTimeout",
  CreditDecrementFailed = "CreditDecrementFailed",
  ProductCouldNotBeParsed = "ProductCouldNotBeParsed",
  PartnershipUnlockLoadFailed = "PartnershipUnlockLoadFailed",
  AbTestingLoadFailed = "AbTestingLoadFailed",
  Unknown = "Unknown",
}

export interface CoreErrorParams {
  cause: CoreErrorCause;
  context?: string;
  underlyingException?: unknown;
  originalError?: unknown;
  reportToSentry?: boolean;
}

export class CoreError extends Error {
  public readonly cause: CoreErrorCause;
  public readonly context?: string;
  public readonly underlyingException?: unknown;

  private static shouldLog(params: CoreErrorParams): boolean {
    if (params.reportToSentry === false) {
      return false;
    }

    const context = params.context ?? "";
    // A partner dissolving the partnership revokes the read rules on the
    // shared nodes (matches, unlocks) and the partner profile at once.
    const isExpectedPartnerRevocation =
      params.cause === CoreErrorCause.PermissionDenied &&
      ((context.includes("permission_denied at /users/") &&
        context.includes("/profile")) ||
        (context.includes("permission_denied at /partnerships/") &&
          context.includes("/matches")) ||
        context.includes("permission_denied at /partnershipUnlocks/"));

    return !isExpectedPartnerRevocation;
  }

  constructor(params: CoreErrorParams) {
    super(params.context ?? params.cause);
    this.cause = params.cause;
    this.context = params.context;
    this.underlyingException =
      params.underlyingException ?? params.originalError;
    this.name = "CoreError";
    const reportToSentry = params.reportToSentry !== false;
    const shouldLog = CoreError.shouldLog(params);

    if (shouldLog) {
      console.error("🚨 CoreError created:", {
        cause: this.cause,
        context: this.context,
        underlyingException: this.underlyingException,
        message: this.message,
        stack: this.stack,
      });
    }

    if (!reportToSentry) {
      // Suppressed on purpose (expected user-flow or environmental outcome).
      // Keep a breadcrumb so it still shows up as context on real errors.
      Sentry.addBreadcrumb({
        category: "core_error.suppressed",
        level: "warning",
        message: this.message,
        data: { cause: this.cause },
      });
    }

    if (reportToSentry) {
      Sentry.withScope((scope) => {
        scope.setTag("error_type", "CoreError");
        scope.setTag("core_error_cause", this.cause);
        scope.setLevel(shouldLog ? "error" : "warning");
        scope.setContext("core_error", {
          cause: this.cause,
          context: this.context ?? "",
          message: this.message,
          shouldLog,
        });
        scope.setExtra("underlyingException", this.underlyingException);
        Sentry.captureException(this);
      });
    }

    const ErrorWithCapture = Error as typeof Error & {
      captureStackTrace?: (
        target: object,
        constructor: new (...args: never[]) => unknown,
      ) => void;
    };
    if (ErrorWithCapture.captureStackTrace) {
      ErrorWithCapture.captureStackTrace(this, CoreError);
    }
  }
}
